import React, { useState, useEffect, ReactNode, useRef } from 'react';
import { FeeConfiguration, PriceCalculationResult } from '@/types';
import { useEffectDebounced, useGetProjectId } from '@/utils/hooks';
import { getFeePreview } from '@/services/fees';
import PriceCalculationDisplay from './PriceCalculationDisplay';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';

type ChildrenType = (calculation: PriceCalculationResult | null, loading: boolean) => ReactNode;

interface FeePreviewProps {
    basePrice: number;
    quantity: number;
    configuration: FeeConfiguration[];
    children?: ChildrenType;
    onUpdate?(calculation: PriceCalculationResult | null): void;
}

const defaultRenderer: ChildrenType = (calculation, loading) =>
    calculation ? <PriceCalculationDisplay value={calculation.steps} loading={loading} /> : <LoadingIndicator />;

const FeePreview: React.FC<FeePreviewProps> = ({
    configuration,
    basePrice,
    quantity,
    children = defaultRenderer,
    onUpdate,
}) => {
    const [calculationResult, setCalculationResult] = useState<PriceCalculationResult | null>(null);
    const [loading, setLoading] = useState(false);
    const projectId = useGetProjectId();
    useEffect(() => {
        setLoading(true);
    }, [configuration, quantity, basePrice]);

    useEffectDebounced(
        () => {
            async function loadData() {
                const safeConfiguration = configuration.map(({ feePercentage, taxPercentage, ...rest }) => ({
                    feePercentage: feePercentage || 0,
                    taxPercentage: taxPercentage || 0,
                    ...rest,
                }));

                var calculation = await getFeePreview(basePrice, quantity, safeConfiguration, projectId);
                setCalculationResult(calculation);
                setLoading(false);
                // triggerHandler(onUpdate, calculation);
            }

            loadData();
        },
        [configuration, quantity, basePrice],
        250
    );

    const oldValue = useRef<PriceCalculationResult | null>(null);
    useEffect(() => {
        if (calculationResult !== oldValue.current) {
            triggerHandler(onUpdate, calculationResult);
            oldValue.current = calculationResult;
        }
    }, [calculationResult, onUpdate]);

    return <>{children(calculationResult, loading)}</>;
};

export default FeePreview;
